/**
 * EditorToolbar — the shared glassy toolbar shell for every editor.
 *
 * Slots:
 *   mode     — left: usually a <ModeToggle/> (any ReactNode). Never collapses.
 *   actions  — middle tools; when the bar is too narrow, the lowest-priority
 *              actions move into a "more" (burger) menu on the right. Fit is
 *              MEASURED (widths cached per key + ResizeObserver), not
 *              breakpoint-guessed.
 *   trailing — right-aligned TOOLS (fullscreen, share, settings…). Same
 *              measured collapse as `actions` — they fold into the burger one
 *              at a time as the bar narrows — they just live on the right.
 *   status   — right-aligned INDICATORS (connection badge, read-only pill).
 *              Never collapses; keep it small.
 *
 * `compactLabels` (on by default; pass false to opt out): before ANY tool folds
 * into the burger, the
 * bar first drops text labels — the mode pill's included — so labeled controls
 * go icon-only (CSS keys on `data-compact`, labels are `.ws-tb-label`). Tools
 * fold only if the icon-only bar still overflows.
 *
 * Why `trailing` exists: editors used to park their right-hand tools in
 * `status`, which is `shrink-0` and exempt from the fit math. Once mode +
 * status exceeded the row, no amount of action-collapsing could recover it and
 * those icons spilled off the right edge of the screen (a phone, or equally a
 * narrow split pane). Tools belong in a slot that can collapse; only
 * indicators stay pinned.
 *
 * Editors keep their bespoke tool logic/handlers — they only hand the
 * rendered nodes to `actions` / `trailing`. The shell owns styling +
 * responsive behavior.
 */
import { useState, useRef, useEffect, useLayoutEffect, createContext, type ReactNode } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { ToolbarPopover } from './ToolbarPopover'
import { ToolbarCompactContext } from './toolbarCompact'

export interface ToolbarAction {
  key: string
  node: ReactNode
  /** Higher stays inline longer when space runs out (default 0) */
  priority?: number
}

/** Lets an action inside the overflow ("more") menu close it on click — e.g. a
 *  one-shot action that opens a panel. `close()` is a no-op when the action is
 *  inline (no menu open), so actions can call it unconditionally. */
export const ToolbarMenuContext = createContext<{ close: () => void }>({ close: () => {} })

const GAP = 8          // .ws-editor-toolbar flex gap
const MORE_BTN_W = 34   // reserved width for the burger button
const TOOLBAR_PAD_X = 24 // matches .ws-editor-toolbar `padding: 0 12px`

export function EditorToolbar({
  mode,
  actions = [],
  trailing = [],
  status,
  onClick,
  className = '',
  compactLabels = true,
}: {
  mode?: ReactNode
  actions?: ToolbarAction[]
  /** Right-aligned tools. Collapse into the burger exactly like `actions`. */
  trailing?: ToolbarAction[]
  status?: ReactNode
  onClick?: React.MouseEventHandler
  className?: string
  /** Drop text labels (icon-only, mode pill included) before folding any tool. On by default. */
  compactLabels?: boolean
}) {
  const rowRef = useRef<HTMLDivElement>(null)
  const modeRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const trailingRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  // One cache per label state: a control measures differently with and without
  // its label, and deciding whether the FULL bar fits again needs full widths
  // while the bar is showing its compact ones.
  const widthCache = useRef({ full: new Map<string, number>(), compact: new Map<string, number>() })
  // Only the WITH-LABELS width, because that is the only one ever read: the
  // compact decision is made from full widths, and the folding maths uses the
  // live offsetWidth of the current state. Keeping a compact twin here would
  // be state nothing consults.
  const modeWidth = useRef<number | undefined>(undefined)
  // `status` is exempt from folding, but NOT from the label switch: the
  // `[data-compact] .ws-tb-label` rule hides labels anywhere in the bar, so a
  // labelled control parked in `status` measures narrower once compact. Cache
  // it per label state like `mode`, or the compact decision compares full
  // widths against a compact status width, flips, re-measures, and flips back
  // — a synchronous loop React ends with "Maximum update depth exceeded".
  const statusWidth = useRef<number | undefined>(undefined)
  /** A slot changed size since the last fit, so the full-width cache — which
   *  CSS is hiding while compact — can no longer be trusted. */
  const slotsResized = useRef(false)
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([])
  const [compact, setCompact] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const burgerBtnRef = useRef<HTMLButtonElement>(null)

  // fit() runs on every resize frame (coalesced to one rAF by the RO). It
  // refreshes the width cache from whatever actions are currently inline —
  // a handful of offsetWidth reads, cheap after the frame's layout — then
  // reads only the row/mode/status extents. Measuring here (not just on
  // action-set change) keeps the cache correct in two cases the old
  // key-only refresh missed: (1) a toolbar first mounted under display:none
  // (inactive/cached workspace tab) whose actions measured 0 and never got
  // a second measure on reveal, and (2) an action whose content changes
  // width under the SAME key (e.g. the HTML editor's root-folder label).
  // Overflowed/hidden actions aren't in the DOM to measure, so they keep
  // their last inline width until they fold back in.
  const fit = () => {
    const row = rowRef.current
    if (!row) return
    const rowInner = row.clientWidth - TOOLBAR_PAD_X
    // Not laid out yet (mounted hidden). Bail — the ResizeObserver refires
    // with a real width on reveal, and a negative rowInner would wrongly
    // fold every action into the burger.
    if (rowInner <= 0) return
    // Measure BOTH collapsible containers — `trailing` lives in its own DOM
    // node on the right but shares the same width cache and burger.
    const measureInto = (target: 'full' | 'compact') => {
      const cache = widthCache.current[target]
      for (const container of [actionsRef.current, trailingRef.current]) {
        container?.querySelectorAll<HTMLElement>('[data-akey]').forEach(el => {
          if (el.offsetWidth > 0) cache.set(el.dataset.akey!, el.offsetWidth)
        })
      }
      if (target === 'full') {
        // No `> 0` guard: fit() has already returned when the ROW has no
        // layout, so a zero here means a slot that genuinely renders nothing —
        // and treating that as "never measured" would leave the label stage
        // permanently switched off for that editor.
        if (modeRef.current) modeWidth.current = modeRef.current.offsetWidth
        if (statusRef.current) statusWidth.current = statusRef.current.offsetWidth
      }
    }
    const state = compact ? 'compact' : 'full'
    measureInto(state)
    // The compact decision is made from FULL widths, which CSS is currently
    // hiding — so while compact they can only be re-read by restoring the
    // labels for the length of one synchronous measure. Nothing paints in
    // between (same layout pass). Skipping this leaves the full cache frozen at
    // whatever the bar last looked like WITH labels: clear the amber "awaiting
    // consent" badge, or lose a mode option, while the bar is icon-only, and it
    // keeps reserving the space that badge used to need — so the labels refuse
    // to come back at a width that now fits them.
    if (compact && slotsResized.current) {
      const prev = row.getAttribute('data-compact')
      row.removeAttribute('data-compact')
      measureInto('full')
      if (prev !== null) row.setAttribute('data-compact', prev)
    }
    slotsResized.current = false
    const modeW = modeRef.current ? modeRef.current.offsetWidth + GAP : 0
    const statusW = statusRef.current ? statusRef.current.offsetWidth + GAP : 0
    // One priority-ordered pool: an action and a trailing tool compete for the
    // same space, so the lowest-priority item folds first regardless of side.
    const pool = [...actions, ...trailing]

    if (!compactLabels && compact) { setCompact(false); return }
    if (compactLabels) {
      // Labels go before any tool folds. Decided from the FULL widths — stable
      // while compact — so a bar right at the threshold can't flap between states.
      const fullWidths = pool.map(a => widthCache.current.full.get(a.key))
      const modeFull = mode ? modeWidth.current : 0
      // Full-width status, for the same reason the tool widths are full ones.
      const statusFull = statusRef.current ? statusWidth.current : 0
      // Full widths are only ever measured while NOT compact. A tool that
      // first appears while the bar is already compact therefore has no full
      // width, the guard below stays false forever, and the bar can never go
      // back to labels even when the window is widened. (Reachable: an editor
      // that adds an action mid-session — HtmlEditor's `design` when the
      // preview turns session-backed, ImageViewer's zoom cluster once the
      // picture loads.) Drop to full for ONE pass with everything inline so
      // the cache learns it; a genuinely-too-narrow bar comes straight back on
      // the next pass, now on real numbers.
      if (compact && (modeFull === undefined || statusFull === undefined || fullWidths.some(w => w === undefined))) {
        setHiddenKeys(prev => (prev.length ? [] : prev))
        setCompact(false)
        return
      }
      if (modeFull !== undefined && statusFull !== undefined && fullWidths.every(w => w !== undefined)) {
        const fullNeed = (mode ? modeFull + GAP : 0) + fullWidths.reduce<number>((s, w) => s + (w ?? 0) + GAP, 0)
        const wantCompact = fullNeed > rowInner - (statusFull ? statusFull + GAP : 0)
        if (wantCompact !== compact) {
          // Switch with every tool inline, so the new state measures all of them.
          setHiddenKeys(prev => (prev.length ? [] : prev))
          setCompact(wantCompact)
          return
        }
      }
    }

    // Folding is laid out in the CURRENT label state, so it uses that cache.
    const widths = pool.map(a => widthCache.current[state].get(a.key) ?? 0)
    const totalAll = widths.reduce((s, w) => s + w + GAP, 0)
    let hidden: string[] = []
    if (totalAll > rowInner - modeW - statusW) {
      // Doesn't fit — reserve the burger and hide lowest-priority (then
      // rightmost) items until it does
      const avail = rowInner - modeW - statusW - MORE_BTN_W - GAP
      const order = pool
        .map((a, i) => ({ a, i, w: widths[i] }))
        .sort((x, y) => (x.a.priority ?? 0) - (y.a.priority ?? 0) || y.i - x.i)
      let total = totalAll
      for (const { a, w } of order) {
        if (total <= avail) break
        hidden.push(a.key)
        total -= w + GAP
      }
    }
    setHiddenKeys(prev =>
      prev.length === hidden.length && prev.every(k => hidden.includes(k)) ? prev : hidden,
    )
  }
  const fitRef = useRef(fit)
  fitRef.current = fit

  // Refit when the action set changes — keyed on a stable signature, not
  // `actions` identity (a new array every parent render, which during a
  // resize drag would refit every frame). fit() itself re-measures inline
  // actions, so this just triggers a synchronous measure+fit for the new set.
  const actionSig = [...actions, ...trailing].map(a => a.key).join('|')
  useLayoutEffect(() => { fitRef.current() }, [actionSig, compact, compactLabels])

  // Keys must be unique ACROSS `actions` and `trailing` — they share one width
  // cache and one `hiddenKeys` set, so a collision silently: collapses both
  // entries together the moment one is hidden, double-counts that width into the
  // fit math (folding the bar more aggressively than it needs to), and emits
  // duplicate React keys in the burger. `trailing` is a new slot and the obvious
  // names (`share`, `export`, `settings`) are exactly the ones an editor would
  // reach for in EITHER, so fail loudly in dev rather than mis-fit quietly.
  if (import.meta.env.DEV) {
    const keys = [...actions, ...trailing].map(a => a.key)
    const dupes = keys.filter((k, i) => keys.indexOf(k) !== i)
    if (dupes.length) {
      console.error(
        `[EditorToolbar] duplicate action key(s) across actions/trailing: ${[...new Set(dupes)].join(', ')}. ` +
        'Keys are the width-cache and overflow identity — they must be unique across BOTH slots.',
      )
    }
  }

  // Resize → refit from cache, coalesced to one rAF per frame so the read
  // lands after the frame's layout instead of forcing a mid-frame reflow.
  //
  // Watched alongside the row, because the row cannot report what matters:
  // `.ws-editor-toolbar` is a fixed-height, full-width strip, so it only
  // resizes when the window does. A control whose CONTENT changes — the amber
  // "awaiting consent" badge clearing, a zoom readout going 100% → 28%, a root
  // path getting shorter — never moves it.
  //
  // What is watched is deliberately the `shrink-0` nodes: the mode and status
  // slots, and each action's own wrapper. NOT the `actions` container, which is
  // `flex-1` — it takes whatever width is left, so it reports nothing about its
  // contents AND changes on every frame of a window drag, which would both miss
  // the real signal and mark every frame as "content changed".
  const hasMode = !!mode
  const hasStatus = !!status
  // Re-observe when the inline set changes: a folded action leaves the DOM, and
  // the one that replaces it needs watching.
  const inlineSig = `${actionSig}|${hiddenKeys.join(',')}`
  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    let raf = 0
    // ResizeObserver delivers one entry per element as soon as it is observed,
    // so "we got an entry" is not "something changed" — and this effect
    // re-observes on every fold. Compare against the last width instead: the
    // first sighting of an element only records it.
    const seen = new Map<Element, number>()
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        // A row-only change is the window moving, which leaves every control
        // the same width; only a watched CONTROL resizing means content
        // changed. The flag is what buys the expensive full re-measure in
        // fit() — see there.
        if (e.target === row) continue
        const w = e.contentRect.width
        if (seen.get(e.target) === w) continue
        if (seen.has(e.target)) slotsResized.current = true
        seen.set(e.target, w)
      }
      if (raf) return
      raf = requestAnimationFrame(() => { raf = 0; fitRef.current() })
    })
    ro.observe(row)
    for (const el of [modeRef.current, statusRef.current]) if (el) ro.observe(el)
    for (const el of row.querySelectorAll('[data-akey]')) ro.observe(el)
    return () => { if (raf) cancelAnimationFrame(raf); ro.disconnect() }
  }, [hasMode, hasStatus, inlineSig])

  // Burger contents, in bar order: actions then trailing.
  const hidden = [...actions, ...trailing].filter(a => hiddenKeys.includes(a.key))

  // Don't let a stale-open burger linger: when the bar widens and nothing
  // overflows, the burger (and its popover) unmounts — reset menuOpen so a
  // later re-narrow doesn't re-open it unbidden.
  useEffect(() => {
    if (menuOpen && hidden.length === 0) setMenuOpen(false)
  }, [menuOpen, hidden.length])

  return (
    <ToolbarMenuContext.Provider value={{ close: () => setMenuOpen(false) }}>
    <ToolbarCompactContext.Provider value={compact}>
    <div ref={rowRef} className={`ws-editor-toolbar ${className}`} data-compact={compact || undefined} onClick={onClick}>
      {mode && <div ref={modeRef} className="flex items-center shrink-0">{mode}</div>}
      {/* Clips the pre-measurement overflow so it never flashes. Action
          dropdowns must NOT render in-flow here (they'd be clipped) — use
          ToolbarPopover, which portals out of this subtree. */}
      <div ref={actionsRef} className="flex-1 min-w-0 flex items-center gap-2 overflow-hidden">
        {/* Render each action node EXACTLY ONCE — inline here, or in the
            burger below, never both. Rendering a node in two places shares
            its refs/state across two DOM instances: the copy that unmounts
            runs ref-cleanup and nulls a ref the surviving copy still needs
            (this silently broke the HTML editor's root popover after it had
            been shown from the burger). Overflowed widths come from the
            cache — every action is inline (and measured) on first mount. */}
        {actions.map(a =>
          hiddenKeys.includes(a.key) ? null : (
            <div key={a.key} data-akey={a.key} className="flex items-center gap-2 shrink-0">
              {a.node}
            </div>
          ),
        )}
      </div>
      {/* Right-aligned tools. `ml-auto` pins the whole right cluster (this +
          the burger + status) to the far edge once the actions area stops
          filling the row. Same data-akey/measure/collapse contract as actions —
          only the position differs. */}
      {trailing.length > 0 && (
        <div ref={trailingRef} className="flex items-center gap-2 shrink-0 ml-auto">
          {trailing.map(a =>
            hiddenKeys.includes(a.key) ? null : (
              <div key={a.key} data-akey={a.key} className="flex items-center gap-2 shrink-0">
                {a.node}
              </div>
            ),
          )}
        </div>
      )}
      {hidden.length > 0 && (
        <div className="relative shrink-0">
          <button
            ref={burgerBtnRef}
            onClick={e => { e.stopPropagation(); setMenuOpen(v => !v) }}
            className="ws-icon-btn ws-tool-toggle"
            data-on={menuOpen ? '' : undefined}
            data-tooltip="More tools"
            aria-label="More tools"
            aria-expanded={menuOpen}
          >
            <MoreHorizontal size={15} />
          </button>
          {/* Portaled (not an in-flow absolute div) so the editor card's
              rounded overflow can't clip it, and so an overflowed action's
              own ToolbarPopover nests without either one dismissing the
              other. */}
          <ToolbarPopover anchorRef={burgerBtnRef} open={menuOpen} onClose={() => setMenuOpen(false)} align="right">
            {/* One ROW, not a column: these are the same icon buttons that sit
                in the bar — stacking them turned a compact icon strip into a
                tall list of mostly-empty rows. `flex-wrap` so a wide member
                (e.g. Export, which carries a label) can't push the row past the
                popover's clamped max-width. */}
            {/* NOT compact in here. The bar's icon-only stage is CSS
                (`.ws-editor-toolbar[data-compact] .ws-tb-label`), a DESCENDANT
                rule — and this popover is portaled out of the bar, so labels
                render in full. React context, though, crosses a portal: left
                on, every control would ALSO offer its visible label as a hover
                hint, i.e. the same words twice. */}
            <ToolbarCompactContext.Provider value={false}>
              <div className="flex flex-row flex-wrap items-center gap-1.5 p-1">
                {hidden.map(a => (
                  <div key={a.key} className="flex items-center gap-2">{a.node}</div>
                ))}
              </div>
            </ToolbarCompactContext.Provider>
          </ToolbarPopover>
        </div>
      )}
      {status && <div ref={statusRef} className="flex items-center gap-2 shrink-0 ml-auto">{status}</div>}
    </div>
    </ToolbarCompactContext.Provider>
    </ToolbarMenuContext.Provider>
  )
}
